import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';
import { cn } from '../lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  inputSize?: 'md' | 'lg';
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const sizes = {
  md: 'h-11 text-base',
  lg: 'h-13 text-md',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, inputSize = 'md', iconLeft, iconRight, ...rest },
  ref,
) {
  if (iconLeft || iconRight) {
    return (
      <div className={cn('relative w-full')}>
        {iconLeft ? (
          <span
            className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-muted [&>svg]:h-4 [&>svg]:w-4"
            aria-hidden
          >
            {iconLeft}
          </span>
        ) : null}
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className={cn(
            'block w-full rounded-sm border bg-surface text-ink placeholder:text-ink-muted',
            'transition-[border-color,box-shadow] duration-fast ease-enter',
            'focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-focus',
            'disabled:bg-surface-sunken disabled:text-ink-disabled disabled:cursor-not-allowed',
            'hover:border-border-strong',
            sizes[inputSize],
            'px-3',
            iconLeft && 'pl-9',
            iconRight && 'pr-9',
            invalid
              ? 'border-error focus-visible:border-error focus-visible:shadow-[0_0_0_4px_var(--color-error-soft)]'
              : 'border-border-soft',
            className,
          )}
          {...rest}
        />
        {iconRight ? (
          <span
            className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-ink-muted [&>svg]:h-4 [&>svg]:w-4"
            aria-hidden
          >
            {iconRight}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'block w-full rounded-sm border bg-surface px-3 text-ink placeholder:text-ink-muted',
        'transition-[border-color,box-shadow] duration-fast ease-enter',
        'focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-focus',
        'disabled:bg-surface-sunken disabled:text-ink-disabled disabled:cursor-not-allowed',
        'hover:border-border-strong',
        sizes[inputSize],
        invalid
          ? 'border-error focus-visible:border-error focus-visible:shadow-[0_0_0_4px_var(--color-error-soft)]'
          : 'border-border-soft',
        className,
      )}
      {...rest}
    />
  );
});
